import { supabase } from './supabase';

function getBaseUrl(): string {
  const raw = (import.meta as any)?.env?.VITE_RAG_API_BASE_URL as string | undefined;
  const fallback = 'https://fcc-summarize-app-r4ac3.ondigitalocean.app';
  return (raw || fallback).replace(/\/+$/, '');
}

async function getAuthToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

// The RAG API shares its worker pool with long synchronous summarization
// batches; while a batch runs, unrelated requests (digest preview/history) can
// block for a long time. Without a client timeout the Watchlist spinners would
// spin forever. Bound every call so the UI can fall back to an error/empty
// state instead. AbortError propagates as a normal rejection to the caller's
// .catch(), which clears the loading flag.
async function fetchWithTimeout(input: string, init: RequestInit = {}, ms = 20000): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(id);
  }
}

async function getAuthContext(): Promise<{ token: string | null; userId: string | null }> {
  const { data } = await supabase.auth.getSession();
  return {
    token: data.session?.access_token ?? null,
    userId: data.session?.user?.id ?? null,
  };
}

async function parseErrorMessage(res: Response): Promise<string> {
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await res.json().catch(() => null as any);
    if (data?.detail) return String(data.detail);
    if (data?.error) return String(data.error);
    return `HTTP ${res.status}: ${res.statusText}`;
  }
  const text = await res.text().catch(() => '');
  return text?.trim() ? text.trim() : `HTTP ${res.status}: ${res.statusText}`;
}

// Digests are scoped to substantive comment filings only:
// filing_type IN ('COMMENT', 'REPLY COMM') AND express_comment = false.
// This filtering is enforced on the backend; the frontend consumes pre-filtered data.

// ── Types ──────────────────────────────────────────────────────────────────

export interface DigestPreviewDocket {
  docket_number: string;
  proceeding_name: string | null;
  new_filings_count: number;
  last_notified_at: string | null;
  top_filers: string[];
  latest_filing_date: string | null;
}

export interface DigestPreview {
  user_id: string;
  dockets: DigestPreviewDocket[];
  total_new_filings: number;
  next_digest_at: string | null;
}

export interface DigestHistoryEntry {
  id: string;
  sent_at: string;
  frequency: string;
  dockets_included: string[];
  total_filings_reported: number;
  status: string;
}

export interface DigestHistoryResponse {
  emails: DigestHistoryEntry[];
}

export interface DigestDeliveryFiling {
  filing_id: string;
  submission_id: string | null;
  docket_number: string;
  filer_name: string | null;
  filing_type: string | null;
  date_received: string | null;
  summary: string | null;
}

// ── API calls ──────────────────────────────────────────────────────────────

export async function getDigestPreview(): Promise<DigestPreview> {
  const baseUrl = getBaseUrl();
  const token = await getAuthToken();

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetchWithTimeout(`${baseUrl}/api/watchlist/digest-preview`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });

  if (!res.ok) {
    const message = await parseErrorMessage(res);
    throw new Error(message);
  }

  const payload = (await res.json()) as Partial<DigestPreview> & {
    dockets?: Array<Partial<DigestPreviewDocket>> | null;
  };
  const rawDockets = Array.isArray(payload.dockets) ? payload.dockets : [];

  const dockets: DigestPreviewDocket[] = rawDockets.map((docket) => ({
    docket_number: typeof docket?.docket_number === 'string' ? docket.docket_number : '',
    proceeding_name: typeof docket?.proceeding_name === 'string' ? docket.proceeding_name : null,
    new_filings_count: typeof docket?.new_filings_count === 'number' ? docket.new_filings_count : 0,
    last_notified_at: typeof docket?.last_notified_at === 'string' ? docket.last_notified_at : null,
    top_filers: Array.isArray(docket?.top_filers)
      ? docket.top_filers.filter((filer): filer is string => typeof filer === 'string')
      : [],
    latest_filing_date: typeof docket?.latest_filing_date === 'string' ? docket.latest_filing_date : null,
  }));

  return {
    user_id: typeof payload.user_id === 'string' ? payload.user_id : '',
    dockets,
    total_new_filings: typeof payload.total_new_filings === 'number' ? payload.total_new_filings : 0,
    next_digest_at: typeof payload.next_digest_at === 'string' ? payload.next_digest_at : null,
  };
}

/**
 * Fetch the filings that were included in a specific digest email the user
 * previously received. Reads directly from Supabase (RLS scopes to the user).
 */
export async function getDigestDeliveryFilings(
  digestEmailLogId: string
): Promise<DigestDeliveryFiling[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('watchlist_digest_deliveries')
    .select(
      `
      filing_id,
      filings:filing_id (
        submission_id,
        docket_number,
        filer_name,
        filing_type,
        date_received,
        summary
      )
    `
    )
    .eq('user_id', user.id)
    .eq('digest_email_log_id', digestEmailLogId);

  if (error) {
    console.error('Error fetching digest delivery filings:', error);
    return [];
  }

  return ((data || []) as Array<{
    filing_id: string;
    filings: {
      submission_id: string | null;
      docket_number: string;
      filer_name: string | null;
      filing_type: string | null;
      date_received: string | null;
      summary: string | null;
    } | null;
  }>).map((row) => ({
    filing_id: row.filing_id,
    submission_id: row.filings?.submission_id ?? null,
    docket_number: row.filings?.docket_number ?? '',
    filer_name: row.filings?.filer_name ?? null,
    filing_type: row.filings?.filing_type ?? null,
    date_received: row.filings?.date_received ?? null,
    summary: row.filings?.summary ?? null,
  }));
}

export async function getDigestHistory(limit = 20): Promise<DigestHistoryResponse> {
  const baseUrl = getBaseUrl();
  const { token, userId } = await getAuthContext();

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // The endpoint requires user_id (Query) and verifies it against the JWT.
  // Omitting it returns 422, which silently hides the "Recent digests" section.
  const params = new URLSearchParams({ limit: String(limit) });
  if (userId) params.set('user_id', userId);

  const res = await fetchWithTimeout(
    `${baseUrl}/api/watchlist/digest-history?${params.toString()}`,
    { headers },
  );

  if (!res.ok) {
    const message = await parseErrorMessage(res);
    throw new Error(message);
  }

  return (await res.json()) as DigestHistoryResponse;
}
